import { useEffect, useState } from 'react';
import { Trans } from 'react-i18next';
import { toast } from 'sonner';
import { Loader2, Trash2, AlertCircle } from 'lucide-react';
import { useTranslation, mapProductError } from '@dukanti/shared';
import type { ProductWithImages, ProductErrorKey } from '@dukanti/shared';
import { useDeleteProduct } from '@/hooks/use-delete-product';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

// `toast.success()` / `toast.error()` are provided by sonner, which is
// mounted once in `apps/admin/src/app.tsx` as `<Toaster position="top-center">`
// (F4d-4). The inline error banner below STILL renders on failure so users
// see the outcome even if the toaster is dismissed.

export interface DeleteProductDialogProps {
  /** The product to delete. When `null`, the dialog is closed. */
  readonly product: ProductWithImages | null;
  /** Store scope for the optimistic-cache key in `useDeleteProduct`. */
  readonly storeId: string;
  /** Controlled open state. */
  readonly open: boolean;
  /** Fires on open/close transitions from the AlertDialog primitive. */
  readonly onOpenChange: (open: boolean) => void;
  /**
   * Called once the delete mutation succeeds. Used by the detail route
   * to navigate back to the products list after the row vanishes.
   */
  readonly onDeleted?: () => void;
}

/**
 * Confirmation dialog for soft-deleting a product.
 *
 * Controlled AlertDialog that:
 *   - Renders the product name inside the description paragraph using
 *     Amiri (serif, semibold) to echo the storefront's warm typography.
 *   - Fires `useDeleteProduct` on confirm. Dialog stays open during the
 *     pending mutation; both buttons disable; the confirm button swaps
 *     its label to "جارٍ الحذف..." with a spinner.
 *   - On success: closes itself, calls `onDeleted`, fires a sonner
 *     success toast with `admin.products.toasts.deleted`.
 *   - On error: keeps the dialog open, renders an inline error banner
 *     with the mapped `ProductErrorKey`, and fires a sonner error toast
 *     so the user can retry.
 *
 * See F-003 design §8.5 for the full spec.
 */
export function DeleteProductDialog({
  product,
  storeId,
  open,
  onOpenChange,
  onDeleted,
}: DeleteProductDialogProps) {
  const { t, dir } = useTranslation();
  const deleteProduct = useDeleteProduct();
  const [errorKey, setErrorKey] = useState<ProductErrorKey | null>(null);

  const isPending = deleteProduct.isPending;

  // Clear any stale error whenever the dialog reopens for a new product.
  // We key on product.id so re-opening on the SAME product after an error
  // preserves the banner until the user retries (expected UX), while a
  // different product starts fresh.
  useEffect(() => {
    if (!open) {
      setErrorKey(null);
    }
  }, [open, product?.id]);

  function handleOpenChange(next: boolean): void {
    // Block closing while pending — the mutation is in flight and the
    // optimistic cache update has already removed the row from the list
    // behind the dialog; letting the user dismiss mid-flight would leave
    // them staring at a disappeared row with no visible outcome.
    if (isPending) return;
    if (!next) setErrorKey(null);
    onOpenChange(next);
  }

  function handleConfirm(): void {
    // The `!product` side is unreachable: line 112 short-circuits the
    // render when product is null, so handleConfirm can never be bound
    // to a button while product is null. Kept as a defensive belt-and-
    // suspenders guard for future refactors.
    /* v8 ignore next */
    if (!product || isPending) return;
    setErrorKey(null);
    deleteProduct.mutate(
      { productId: product.id, storeId },
      {
        onSuccess: () => {
          toast.success(t('admin.products.toasts.deleted'));
          onOpenChange(false);
          onDeleted?.();
        },
        onError: (err) => {
          const mapped = mapProductError(err);
          setErrorKey(mapped);
          toast.error(t(mapped));
        },
      },
    );
  }

  // Nothing to render when there's no product in context. Keeps
  // <AlertDialog /> unmounted so it can't steal focus while idle.
  if (!product) return null;

  return (
    <AlertDialog open={open} onOpenChange={handleOpenChange}>
      <AlertDialogContent dir={dir} data-testid="delete-product-dialog" className="gap-0 p-8">
        <AlertDialogHeader className="mb-3">
          <AlertDialogTitle data-testid="delete-product-dialog-title">
            {t('admin.products.delete.title')}
          </AlertDialogTitle>
        </AlertDialogHeader>

        <AlertDialogDescription
          data-testid="delete-product-dialog-description"
          className="text-sand-600 mb-6 text-sm leading-relaxed"
        >
          <Trans
            i18nKey="admin.products.delete.description"
            values={{ name: product.name }}
            components={{
              name: (
                <strong
                  className="font-display text-sand-700 font-semibold"
                  data-testid="delete-product-dialog-name"
                />
              ),
            }}
          />
        </AlertDialogDescription>

        {errorKey ? (
          <div
            role="alert"
            data-testid="delete-product-dialog-error"
            className={cn(
              'border-terracotta/30 bg-terracotta/10 text-sand-700 rounded-card mb-6',
              'flex items-start gap-2 border p-3 text-start text-sm',
            )}
          >
            <AlertCircle
              aria-hidden="true"
              className="text-terracotta mt-0.5 size-4 shrink-0"
              strokeWidth={1.75}
            />
            <span>{t(errorKey)}</span>
          </div>
        ) : null}

        <AlertDialogFooter>
          <Button
            type="button"
            variant="ghost"
            disabled={isPending}
            onClick={() => handleOpenChange(false)}
            data-testid="delete-product-dialog-cancel"
            className="text-sand-600 hover:bg-sand-100 hover:text-sand-700"
          >
            {t('admin.products.delete.cancel')}
          </Button>
          <Button
            type="button"
            variant="destructive"
            disabled={isPending}
            onClick={handleConfirm}
            data-testid="delete-product-dialog-confirm"
            aria-busy={isPending || undefined}
          >
            {isPending ? (
              <>
                <Loader2 aria-hidden="true" className="size-4 animate-spin" strokeWidth={1.75} />
                <span>{t('admin.products.delete.confirming')}</span>
              </>
            ) : (
              <>
                <Trash2 aria-hidden="true" className="size-4" strokeWidth={1.75} />
                <span>{t('admin.products.delete.confirm')}</span>
              </>
            )}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
